import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/lib/site";

// bento spans for visual rhythm (7 items)
const SPANS = [
  "md:col-span-2 md:row-span-2",
  "",
  "",
  "md:col-span-2",
  "",
  "",
  "md:col-span-2",
];

export default function Gallery() {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const next = useCallback(() => setOpen((i) => (i + 1) % GALLERY.length), []);
  const prev = useCallback(() => setOpen((i) => (i - 1 + GALLERY.length) % GALLERY.length), []);

  useEffect(() => {
    if (open === null) {
      window.__lenis?.start();
      return;
    }
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, next, prev]);

  return (
    <section id="fleet" data-testid="gallery" className="border-t border-white/10">
      <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end"
        >
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-brand">Real Equipment</span>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
              The Fleet
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#a1a1a8]">
            Late-model, well-kept trucks running out of Houston. Tap any photo for a closer look.
          </p>
        </motion.div>

        <div className="grid auto-rows-[220px] grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          {GALLERY.map((img, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.06 }}
              onClick={() => setOpen(i)}
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden border border-white/10 ${SPANS[i] || ""}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-ink/0 transition-colors duration-300 group-hover:bg-ink/30" />
              <div className="absolute inset-x-0 bottom-0 translate-y-2 p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <span className="text-xs uppercase tracking-widest text-white">{img.alt}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm"
            onClick={close}
          >
            <button
              onClick={close}
              data-testid="gallery-close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:border-brand hover:text-brand"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors hover:border-brand hover:text-brand md:left-8"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors hover:border-brand hover:text-brand md:right-8"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.img
              key={open}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              src={GALLERY[open].src}
              alt={GALLERY[open].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] border border-white/10 object-contain"
            />
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-widest text-[#a1a1a8]">
              {open + 1} / {GALLERY.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
